const encoder = new TextEncoder();

export interface OidcConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
  postLogoutRedirectUri: string;
}

export interface OidcTransaction {
  state: string;
  verifier: string;
  returnTo: string;
}

export interface OidcTokens {
  accessToken: string;
  refreshToken: string;
  idToken: string;
  expiresAt: number;
  refreshExpiresAt: number;
}

interface TokenResponse {
  access_token: string;
  refresh_token: string;
  id_token: string;
  expires_in: number;
  refresh_expires_in: number;
}

function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

function randomUrlSafe(bytes = 48): string {
  return base64Url(crypto.getRandomValues(new Uint8Array(bytes)));
}

function tokenEndpoint(issuer: string): string {
  return `${issuer.replace(/\/$/, "")}/protocol/openid-connect/token`;
}

export async function createPkce(): Promise<{verifier: string; challenge: string}> {
  const verifier = randomUrlSafe();
  const digest = await crypto.subtle.digest("SHA-256", encoder.encode(verifier));
  return {verifier, challenge: base64Url(new Uint8Array(digest))};
}

export async function beginAuthorization(
  config: OidcConfig,
  returnTo: string
): Promise<{authorizationUrl: string; transaction: OidcTransaction}> {
  const pkce = await createPkce();
  const state = randomUrlSafe(32);
  const url = new URL(`${config.issuer.replace(/\/$/, "")}/protocol/openid-connect/auth`);
  url.search = new URLSearchParams({
    client_id: config.clientId,
    redirect_uri: config.redirectUri,
    response_type: "code",
    scope: "openid profile email",
    state,
    code_challenge: pkce.challenge,
    code_challenge_method: "S256"
  }).toString();
  return {
    authorizationUrl: url.toString(),
    transaction: {state, verifier: pkce.verifier, returnTo}
  };
}

export async function exchangeAuthorizationCode(
  config: OidcConfig,
  code: string,
  verifier: string,
  now = Date.now()
): Promise<OidcTokens> {
  const response = await fetch(tokenEndpoint(config.issuer), {
    method: "POST",
    headers: {"Content-Type": "application/x-www-form-urlencoded"},
    body: new URLSearchParams({
      grant_type: "authorization_code",
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      code,
      code_verifier: verifier
    })
  });
  return tokens(response, now);
}

export async function refreshTokens(
  config: OidcConfig,
  refreshToken: string,
  now = Date.now()
): Promise<OidcTokens> {
  const response = await fetch(tokenEndpoint(config.issuer), {
    method: "POST",
    headers: {"Content-Type": "application/x-www-form-urlencoded"},
    body: new URLSearchParams({
      grant_type: "refresh_token",
      client_id: config.clientId,
      refresh_token: refreshToken
    })
  });
  return tokens(response, now);
}

async function tokens(response: Response, now: number): Promise<OidcTokens> {
  if (!response.ok) throw new Error(`OIDC token request failed: ${response.status}`);
  const value = await response.json() as Partial<TokenResponse>;
  if (!value.access_token || !value.refresh_token || !value.id_token
      || !value.expires_in || !value.refresh_expires_in) {
    throw new Error("OIDC response omitted required session tokens");
  }
  return {
    accessToken: value.access_token,
    refreshToken: value.refresh_token,
    idToken: value.id_token,
    expiresAt: now + value.expires_in * 1000,
    refreshExpiresAt: now + value.refresh_expires_in * 1000
  };
}

export function logoutUrl(config: OidcConfig, idToken: string): string {
  const url = new URL(`${config.issuer.replace(/\/$/, "")}/protocol/openid-connect/logout`);
  url.search = new URLSearchParams({
    client_id: config.clientId,
    id_token_hint: idToken,
    post_logout_redirect_uri: config.postLogoutRedirectUri
  }).toString();
  return url.toString();
}

export function jwtClaims(token: string): Record<string, unknown> {
  const segments = token.split(".");
  if (segments.length !== 3) throw new Error("JWT is malformed");
  const encoded = segments[1].replaceAll("-", "+").replaceAll("_", "/");
  const padded = encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=");
  return JSON.parse(atob(padded)) as Record<string, unknown>;
}
